import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { UpdateItemDto } from "@/core/features/items/update-item.dto";
import { CreateItemDto } from "@/core/features/items/create-item.dto";
import { ItemDto } from "@/core/features/items/item.dto";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { UpdateItemArchivedStatusDto } from "@/core/features/items/update-item-archived-status.dto";
import { CreateFileDto } from "@/core/features/file/create-file.dto";
import { InvoiceDto } from "@/core/features/invoice/invoice.dto";
import { DeleteFileDto } from "@/core/features/file/delete-file.dto";
import { IRequest } from "@/interfaces/IRequest";
import { OptionType } from "@/types/OptionType";
import { merge } from "lodash";
import { ItemRecordDto } from "@/core/features/items/item-record.dto";
import { MoveItemDto } from "@/core/features/items/move-item.dto";
import { downloadPdfBlob } from "@/utils/fileUtils";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";

export class ItemsResource extends ResourceBaseService<ItemDto, CreateItemDto, UpdateItemDto> {
  protected EntityDto = ItemDto;
  protected CreateEntityDto = CreateItemDto;
  protected UpdateEntityDto = UpdateItemDto;
  protected RecordEntityDto = ItemRecordDto;

  constructor(protected readonly apiHttClient: ApiHttpClient) {
    super(apiHttClient, "/items");
  }

  async optionLoader(inputValue: string, customRequest: IRequest = {}): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "code", "serialNumber"] } : {};
    request.sortBy = "code";
    const { data } = await this.findAll(applyOptionLoaderPagination(merge(request, customRequest)));
    return data.map((value) => value.mapEntityToOptionType([value.toString()]));
  }

  async updateArchiveStatus(id: string, body: UpdateItemArchivedStatusDto): Promise<ItemDto> {
    return this.apiHttpClient.request<UpdateItemArchivedStatusDto, ItemDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/archive",
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateItemArchivedStatusDto, { ...body, id }, { excludeExtraneousValues: true }),
      })
    );
  }

  createManyAttachments(id: string, files: CreateFileDto[]): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "POST",
        auth: true,
        body: { bulk: files },
      })
    );
  }

  deleteAttachment(id: string, deleteFile: DeleteFileDto): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "DELETE",
        auth: true,
        body: deleteFile,
      })
    );
  }

  async moveItem(id: string, body: MoveItemDto): Promise<ItemDto> {
    return this.apiHttpClient.request<MoveItemDto, ItemDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/move",
        method: "PATCH",
        auth: true,
        body: plainToInstance(MoveItemDto, body, { excludeExtraneousValues: true }),
      })
    );
  }

  async exportQrCodes(request: IRequest): Promise<void> {
    const data = await this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/qr-codes",
        method: "GET",
        auth: true,
        query: request ? this.getQueryBuilder(request).query() : null,
      })
    );

    downloadPdfBlob(String(data), "Codigos QR");
  }
}
